import graphql from 'babel-plugin-relay/macro'
import { useAppToDatabaseRename } from 'components/hooks'
import { Container } from 'components/layout'
import { useApplyTenant } from 'components/tenant'
import { useAppContext } from 'context'
import paths from 'paths'
import { Fragment, Suspense, useEffect, useMemo } from 'react'
import {
  PreloadedQuery,
  usePreloadedQuery,
  useQueryLoader,
} from 'react-relay/hooks'
import { Outlet, useLocation, useParams } from 'react-router-dom'

import { UserManagementLayoutQuery } from './__generated__/UserManagementLayoutQuery.graphql'
import UserManagementHeader from './UserManagementHeader'
import UserManagementNavigation from './UserManagementNavigation'

const getSelfPaths = (isRenamed?: boolean) => [
  paths.userApps({ isRenamed }),
  paths.userOrganizations({}),
  paths.userTenant({ area: 'employee-groups' }),
  paths.settings(),
]

const getManagementPaths = (userId: string, isRenamed?: boolean) => [
  paths.userApps({ userId, isRenamed }),
  paths.userOrganizations({ userId }),
  paths.userTenant({ userId, area: 'employee-groups' }),
]

// Backwards compatibility exports for tests
export const selfPaths = getSelfPaths()
export const managementPaths = (userId: string) => getManagementPaths(userId)

const query = graphql`
  query UserManagementLayoutQuery(
    $userId: ID!
    $skipUser: Boolean!
    $skipMe: Boolean!
  ) {
    viewer {
      me @skip(if: $skipMe) {
        # eslint-disable-next-line relay/unused-fields
        id
        isSuperAdmin
        ...UserManagementHeader_userInfo
      }
      user(id: $userId) @skip(if: $skipUser) {
        # eslint-disable-next-line relay/unused-fields
        id
        isSuperAdmin
        ...UserManagementHeader_userInfo
      }
    }
  }
`

export default function UserManagementLayoutContainer() {
  const { userId } = useParams<{ userId: string }>()

  const [queryRef, loadQuery] = useQueryLoader<UserManagementLayoutQuery>(query)

  useEffect(() => {
    const skipMe = !!userId
    const skipUser = !userId
    loadQuery(
      {
        userId: userId || '',
        skipMe,
        skipUser,
      },
      { fetchPolicy: 'store-and-network' },
    )
  }, [loadQuery, userId])

  if (!queryRef) {
    return null
  }

  return <UserManagementLayout queryRef={queryRef} />
}

type Props = {
  queryRef: PreloadedQuery<UserManagementLayoutQuery>
}
function UserManagementLayout({ queryRef }: Props) {
  const location = useLocation()
  const { userId } = useParams<{ userId: string }>()
  const { isRenamed } = useAppToDatabaseRename()

  const { isSuperAdmin } = useAppContext()
  const { viewer } = usePreloadedQuery(query, queryRef)

  useApplyTenant(null)

  // Replace the trailing slash with an empty string, if the path is not the root.
  // This is to prevent the layout from being hidden e.g. on /settings/ or /apps/
  const adjustedPath = useMemo(
    () =>
      location.pathname === '/'
        ? location.pathname
        : location.pathname.replace(/\/$/, ''),
    [location.pathname],
  )
  const showUserManagement = useMemo(() => {
    const validPaths = [
      ...getSelfPaths(isRenamed),
      ...(userId ? getManagementPaths(userId, isRenamed) : []),
    ]
    return validPaths.includes(adjustedPath)
  }, [adjustedPath, userId, isRenamed])

  const hasSomeUser = !!viewer.me || !!viewer.user
  const someUserIsSuperAdmin =
    viewer.me?.isSuperAdmin || viewer.user?.isSuperAdmin || false
  const OutletContainer = showUserManagement ? Container : Fragment
  const containerProps = showUserManagement ? { className: 'py-8' } : {}

  const showEmployeeGroups = isSuperAdmin && someUserIsSuperAdmin

  return (
    <>
      {showUserManagement && (
        <>
          {hasSomeUser && (
            <UserManagementHeader user={(viewer.me ?? viewer.user)!} />
          )}
          <UserManagementNavigation showEmployeeGroups={showEmployeeGroups} />
        </>
      )}
      <Suspense fallback={null} data-testid="userManagementLayoutSuspense">
        <OutletContainer {...containerProps}>
          <Outlet />
        </OutletContainer>
      </Suspense>
    </>
  )
}
